import React, { Component } from 'react';
import { inject, observer } from 'mobx-react';
import { Select } from 'antd';
import { PulseLoader } from 'react-spinners';
import LoadingOverlay from 'react-loading-overlay';
import GoogleMaps from '../../../../../common/components/GoogleMaps';
import regions, { otherRegion } from '../../installersRegions';

import './InstallersSection.css';

const { Option } = Select;

const ListIcon = () => (
  <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M8 6h13M8 12h13M8 18h13M3 6h.01M3 12h.01M3 18h.01" />
  </svg>
);

const MapIcon = () => (
  <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M9 3 3 6v15l6-3 6 3 6-3V3l-6 3-6-3z" />
    <path d="M9 3v15M15 6v15" />
  </svg>
);

const SearchIcon = () => (
  <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="var(--teal-d)" strokeWidth="2" style={{ flex: 'none' }}>
    <circle cx="11" cy="11" r="7" />
    <path d="m21 21-4.3-4.3" />
  </svg>
);

const DEFAULT_CENTER = { lat: 45.233034, lng: 8.831586 };

@inject('translationsStore')
@observer
class InstallersSection extends Component {
  constructor(props) {
    super(props);
    this.state = {
      region: 'lombardia',
      province: undefined,
      query: '',
      view: 'list'
    };
  }

  // Memoized against the `installers`/`currentRegion` references: region and province
  // lists only need to be rescanned when installers reload from the API or the region
  // selection changes, not on every keystroke in the search box.
  getAvailableRegions() {
    const { installers } = this.props.dataCollection;
    if (this._availableRegionsCache && this._availableRegionsCache.installers === installers) {
      return this._availableRegionsCache.result;
    }
    const extra = otherRegion(installers);
    const result = extra ? [...regions, extra] : regions;
    this._availableRegionsCache = { installers, result };
    return result;
  }

  getCurrentRegion(availableRegions) {
    return availableRegions.find(region => region.key === this.state.region) || availableRegions[0];
  }

  getRegionProvinces(currentRegion) {
    const { installers, installersData } = this.props.dataCollection;
    if (this._regionProvincesCache && this._regionProvincesCache.installers === installers && this._regionProvincesCache.currentRegion === currentRegion) {
      return this._regionProvincesCache.result;
    }
    const withInstallers = new Set(installers.map(installer => installer.admdiv2));
    const result = currentRegion.provinces
      .filter(code => withInstallers.has(code))
      .map(code => {
        const area = installersData.areas.find(area => area.code === code);
        return { code, label: area ? area.value : code };
      })
      .sort((a, b) => a.label.localeCompare(b.label));
    this._regionProvincesCache = { installers, currentRegion, result };
    return result;
  }

  getFilteredInstallers(currentRegion) {
    const { installers } = this.props.dataCollection;
    const { province, query } = this.state;
    const provinces = currentRegion.provinces;
    const normalizedQuery = query.trim().toLowerCase();
    return installers
      .filter(installer => provinces.includes(installer.admdiv2))
      .filter(installer => (province ? installer.admdiv2 === province : true))
      .filter(installer =>
        normalizedQuery === ''
          ? true
          : String(installer.admdiv3).toLowerCase().includes(normalizedQuery) || String(installer.postCode).startsWith(normalizedQuery)
      );
  }

  onRegionChange = regionKey => {
    this.setState({ region: regionKey, province: undefined, query: '' });
  };

  render() {
    const { translationsStore, dataCollection } = this.props;
    const { installersLoading } = dataCollection;
    const { region, province, query, view } = this.state;
    const availableRegions = this.getAvailableRegions();
    const currentRegion = this.getCurrentRegion(availableRegions);
    const regionProvinces = this.getRegionProvinces(currentRegion);
    const filtered = this.getFilteredInstallers(currentRegion);
    const provinceLabel = province ? (regionProvinces.find(item => item.code === province) || {}).label : null;
    const mapCenter = filtered.length > 0 && filtered[0].lat ? { lat: Number(filtered[0].lat), lng: Number(filtered[0].lng) } : DEFAULT_CENTER;

    return (
      <div className="InstallersSection">
        <div className="InstallersSectionHead">
          <h2>{translationsStore.translate('movein.stepWelcome.installers.title')}</h2>
          <span>{translationsStore.translate('movein.stepWelcome.installers.subtitle')}</span>
        </div>
        <div className="InstallersSectionBody">
          <LoadingOverlay
            active={installersLoading}
            spinner={<PulseLoader sizeUnit={'px'} size={10} color={'var(--action-primary-color)'} loading={true} />}
            styles={{
              overlay: base => ({
                ...base,
                background: 'var(--grey-light-color-50)'
              })
            }}
          >
            <div className="InstallersFilters">
              <div className="InstallersFilter">
                <label>{translationsStore.translate('movein.stepWelcome.installers.regionLabel')}</label>
                <Select value={region} onChange={this.onRegionChange} dropdownMatchSelectWidth={false}>
                  {availableRegions.map(item => (
                    <Option key={item.key} value={item.key}>
                      {item.label}
                    </Option>
                  ))}
                </Select>
              </div>
              <div className="InstallersFilter">
                <label>{translationsStore.translate('movein.stepWelcome.installers.provinceLabel')}</label>
                <Select
                  value={province}
                  onChange={value => this.setState({ province: value })}
                  allowClear
                  placeholder={translationsStore.translate('movein.stepWelcome.installers.allProvinces')}
                  dropdownMatchSelectWidth={false}
                >
                  {regionProvinces.map(item => (
                    <Option key={item.code} value={item.code}>
                      {`${item.label} (${item.code})`}
                    </Option>
                  ))}
                </Select>
              </div>
              <div className="InstallersFilter InstallersFilterSearch">
                <label>{translationsStore.translate('movein.stepWelcome.installers.searchLabel')}</label>
                <div className="InstallersSearchBox">
                  <SearchIcon />
                  <input
                    type="text"
                    placeholder={translationsStore.translate('movein.stepWelcome.installers.searchPlaceholder')}
                    value={query}
                    onChange={e => this.setState({ query: e.target.value })}
                  />
                </div>
              </div>
            </div>
            <div className="InstallersRegionPills">
              <span className="InstallersRegionPillsLabel">{translationsStore.translate('movein.stepWelcome.installers.regionsPillsLabel')}</span>
              {availableRegions.map(item => (
                <span
                  key={item.key}
                  className={`InstallersRegionPill${item.key === region ? ' active' : ''}`}
                  onClick={() => this.onRegionChange(item.key)}
                >
                  {item.label}
                </span>
              ))}
            </div>
            <div className="InstallersResultsHead">
              <span className="InstallersResultsTitle">
                {translationsStore.translate('movein.stepWelcome.installers.centersIn')}{' '}
                {provinceLabel ? `${provinceLabel} (${province})` : currentRegion.label}
              </span>
              <div className="InstallersResultsTools">
                <span className="InstallersResultsCount">
                  {filtered.length} {translationsStore.translate('movein.stepWelcome.installers.resultsCount')}
                </span>
                <div className="InstallersViewToggle">
                  <span className={view === 'list' ? 'active' : ''} onClick={() => this.setState({ view: 'list' })}>
                    <ListIcon />
                    {translationsStore.translate('movein.stepWelcome.installers.listTab')}
                  </span>
                  <span className={view === 'map' ? 'active' : ''} onClick={() => this.setState({ view: 'map' })}>
                    <MapIcon />
                    {translationsStore.translate('movein.stepWelcome.installers.mapTab')}
                  </span>
                </div>
              </div>
            </div>
            {view === 'list' ? (
              <div className="InstallersList">
                {filtered.length === 0 && <div className="InstallersEmpty">{translationsStore.translate('movein.stepWelcome.installers.noResults')}</div>}
                {filtered.map((installer, index) => (
                  <div className="InstallersListRow" key={`${installer.code}-${index}`}>
                    <div className="InstallersListRowMain">
                      <span className="InstallersAvatar">
                        {String(installer.businessName || '')
                          .split(/\s+/)
                          .slice(0, 2)
                          .map(word => word.charAt(0))
                          .join('')
                          .toUpperCase()}
                      </span>
                      <div>
                        <div className="InstallersListRowName">{installer.businessName}</div>
                        <div className="InstallersListRowInfo">
                          {installer.admdiv3} · {installer.address}
                          {installer.phone ? ` · TEL: ${installer.phone}` : ''}
                        </div>
                      </div>
                    </div>
                    <span className="InstallersProvinceChip">{installer.admdiv2}</span>
                  </div>
                ))}
              </div>
            ) : (
              <div className="InstallersMap">
                <GoogleMaps className="InstallersSectionMap" zoom={8} initialCenter={mapCenter} markers={filtered} />
              </div>
            )}
          </LoadingOverlay>
        </div>
      </div>
    );
  }
}

export default InstallersSection;
